import cx from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ECampaignStatus } from 'types/campaign';

import {
  Drawer, IconButton, Tooltip, useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import CopyIcon from '@material-ui/icons/FileCopyOutlined';
import HomeIcon from '@material-ui/icons/HomeOutlined';
import LocationIcon from '@material-ui/icons/LocationOnOutlined';
import EmailIcon from '@material-ui/icons/MailOutline';
import PersonIcon from '@material-ui/icons/PersonOutline';
import PhoneIcon from '@material-ui/icons/PhoneOutlined';

import EventTypeBadge from 'components/EventTypeBadge';

import { enumToReadableName, getEventDataOwnerInformation, stringifyEventSiteAddressObject } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '420px',
    maxWidth: '100vw',
    color: theme.palette.grayScale9.main,
    backgroundColor: theme.palette.white.main,

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: 'auto',
      maxHeight: '85vh',
      borderRadius: '16px 16px 0 0',
    },
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '24px',
    borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
    backgroundColor: theme.palette.white.main,

    [theme.breakpoints.down('sm')]: {
      padding: '18px 16px 16px',
    },
  },
  title: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.grayScale9.main,
  },
  subtitle: {
    margin: '5px 0 0',
    fontSize: '14px',
    lineHeight: 1.4,
    color: theme.palette.lightGray.main,
  },
  closeButton: {
    width: '44px',
    height: '44px',
    flexShrink: 0,
    color: theme.palette.grayScale11.main,
  },
  body: {
    padding: '8px 24px calc(24px + env(safe-area-inset-bottom))',

    [theme.breakpoints.down('sm')]: {
      padding: '4px 16px calc(20px + env(safe-area-inset-bottom))',
    },
  },
  section: {
    padding: '20px 0',
    borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
  },
  sectionLast: {
    borderBottom: 0,
  },
  sectionTitle: {
    margin: '0 0 12px',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.lightGray.main,
  },
  detailRow: {
    display: 'grid',
    gridTemplateColumns: '24px minmax(0, 1fr) 44px',
    alignItems: 'center',
    gap: '10px',
    minHeight: '54px',

    '& + &': {
      marginTop: '4px',
    },
  },
  detailIcon: {
    color: theme.palette.primary.main,
  },
  detailContent: {
    minWidth: 0,
    userSelect: 'text',
  },
  detailLabel: {
    display: 'block',
    marginBottom: '2px',
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.lightGray.main,
  },
  detailValue: {
    display: 'block',
    overflowWrap: 'anywhere',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.45,
    color: theme.palette.grayScale9.main,
  },
  detailLink: {
    textDecoration: 'underline',
    textUnderlineOffset: '2px',

    '&:hover, &:focus': {
      color: theme.palette.primary.dark,
    },
  },
  copyButton: {
    width: '44px',
    height: '44px',
    color: theme.palette.primary.dark,
  },
  campaignHeading: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  campaignChips: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: '6px',

    '& > span': {
      fontSize: '11px',
      lineHeight: 1.2,
    },
  },
  campaignName: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '44px',
    margin: '6px 0 0',
    fontSize: '17px',
    fontWeight: 700,
    lineHeight: 1.35,
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    cursor: 'pointer',
    transition: 'color 180ms ease',

    '&:hover': {
      color: theme.palette.primary.main,
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '3px',
      borderRadius: '2px',
    },
  },
  campaignLinkIcon: {
    marginLeft: '2px',
    flexShrink: 0,
    fontSize: '20px',
  },
  campaignStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 8px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    color: theme.palette.white.main,
    backgroundColor: theme.palette.orange.main,
  },
  campaignStatusPaused: {
    color: theme.palette.grayScale11.main,
    backgroundColor: theme.palette.grayScale6.main,
  },
  visuallyHidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}));

export const copyTextToClipboard = async (text) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let didCopy = false;

  try {
    didCopy = document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }

  if (!didCopy) throw new Error('Unable to copy text');
};

const CopyableDetailRow = ({
  classes, icon, label, value, href, copyable = true,
}) => {
  if (!value) return null;

  const onCopy = async () => {
    try {
      await copyTextToClipboard(value);
      toast.success(`${label} copied`);
    } catch (err) {
      console.error(err);
      toast.error(`Unable to copy ${label.toLowerCase()}`);
    }
  };

  const renderedValue = href ? (
    <a className={cx(classes.detailValue, classes.detailLink)} href={href}>{value}</a>
  ) : (
    <span className={classes.detailValue}>{value}</span>
  );

  return (
    <div className={classes.detailRow}>
      {React.cloneElement(icon, { className: classes.detailIcon, fontSize: 'small' })}
      <div className={classes.detailContent}>
        <span className={classes.detailLabel}>{label}</span>
        {renderedValue}
      </div>
      {copyable ? (
        <Tooltip title={`Copy ${label}`} placement="top">
          <IconButton
            className={classes.copyButton}
            aria-label={`Copy ${label}`}
            onClick={onCopy}
          >
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : <span />}
    </div>
  );
};

const EventDataDetailsDrawer = ({ eventData, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    if (!eventData || isMobile) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [eventData, isMobile, onClose]);

  if (!eventData) return null;

  const siteAddress = stringifyEventSiteAddressObject(eventData.siteAddress);
  const {
    name, phoneNumber, email, ownerAddress,
  } = getEventDataOwnerInformation(eventData.existingEventOverride, eventData);
  const ownerName = name?.trim() || 'Owner information unavailable';
  const phoneHref = phoneNumber ? `tel:${phoneNumber.replace(/[^\d+]/g, '')}` : undefined;
  const emailHref = email ? `mailto:${email}` : undefined;
  const hasCampaignInfo = !!eventData.campaignId;
  const isPausedCampaign = eventData.campaignStatus === ECampaignStatus.Paused;
  const campaignStatus = eventData.campaignStatus ? enumToReadableName(eventData.campaignStatus) : 'Active';

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      variant={isMobile ? 'temporary' : 'persistent'}
      open
      onClose={onClose}
      classes={{ paper: classes.paper }}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        role: isMobile ? 'dialog' : 'complementary',
        'aria-modal': isMobile || undefined,
        'aria-labelledby': 'event-data-details-title',
      }}
    >
      <div className={classes.visuallyHidden} aria-live="polite">
        {`Property details opened for ${siteAddress}`}
      </div>
      <header className={classes.header}>
        <div>
          <h2 id="event-data-details-title" className={classes.title}>Property details</h2>
          <p className={classes.subtitle}>
            {isMobile ? 'Close this panel to select another marker.' : 'Select another marker to view a different property.'}
          </p>
        </div>
        <IconButton className={classes.closeButton} aria-label="Close property details" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </header>

      <div className={classes.body}>
        <section className={classes.section} aria-labelledby="property-section-title">
          <h3 id="property-section-title" className={classes.sectionTitle}>Property</h3>
          <CopyableDetailRow classes={classes} icon={<LocationIcon />} label="Site Address" value={siteAddress} />
          <CopyableDetailRow classes={classes} icon={<HomeIcon />} label="APN" value={eventData.apn} />
        </section>

        <section className={classes.section} aria-labelledby="owner-section-title">
          <h3 id="owner-section-title" className={classes.sectionTitle}>Owner</h3>
          <CopyableDetailRow classes={classes} icon={<PersonIcon />} label="Owner Name" value={ownerName} copyable={false} />
          <CopyableDetailRow classes={classes} icon={<PhoneIcon />} label="Phone Number" value={phoneNumber} href={phoneHref} />
          <CopyableDetailRow classes={classes} icon={<EmailIcon />} label="Email Address" value={email} href={emailHref} />
          <CopyableDetailRow classes={classes} icon={<HomeIcon />} label="Mailing Address" value={ownerAddress} />
        </section>

        {hasCampaignInfo && (
          <section className={cx(classes.section, classes.sectionLast)} aria-labelledby="campaign-section-title">
            <div className={classes.campaignHeading}>
              <h3 id="campaign-section-title" className={classes.sectionTitle}>Campaign</h3>
              <div className={classes.campaignChips}>
                {eventData.eventTypeName && <EventTypeBadge eventType={eventData.eventTypeName} />}
                <span className={cx(classes.campaignStatus, isPausedCampaign && classes.campaignStatusPaused)}>
                  {campaignStatus}
                </span>
              </div>
            </div>
            <Link
              className={classes.campaignName}
              to={`/dashboard/campaigns/${eventData.campaignId}`}
              onClick={onClose}
            >
              {eventData.campaignName || 'View campaign'}
              <ChevronRightIcon className={classes.campaignLinkIcon} aria-hidden="true" />
            </Link>
          </section>
        )}
      </div>
    </Drawer>
  );
};

export default EventDataDetailsDrawer;
